import { bisect, max, min, range as d3range } from "d3-array";
import { scaleBand, scaleLinear, scalePoint, scaleTime } from "d3-scale";

type Position = [number, number];
export type Range = [number, number];

const scaleTypes: Record<string, CallableFunction> = {
  point: scalePoint,
  band: scaleBand,
  time: scaleTime,
  linear: scaleLinear,
};

const createScale = (
  scaleType: keyof typeof scaleTypes,
  { domain, range, nice, tickCount, padding = 0 },
) => {
  const scale = scaleTypes[scaleType]().domain(domain).rangeRound(range);
  if (tickCount) scale.ticks(tickCount);
  if (nice !== undefined && nice !== false && scale.nice) scale.nice();
  if (scaleType === "band") scale.padding(padding);
  return scale;
};

const DEFAULT_PADDING = 0.2;
const getMostSignificant = (num: number) =>
  Math.log(num) < 0 ? Math.ceil(Math.abs(Math.log10(num))) : 0;

const getChartConfigs = ([min, max]: Range) => {
  const padding = DEFAULT_PADDING;
  let places = getMostSignificant(max);
  places = places > 1 ? 2 : places;
  const offset = (max - min) * padding;
  max += offset;
  min -= offset;
  return [max, min, max - min, places];
};

function getTickValues(yDomain: Range, numIntervals = 8) {
  const [, min, amplitude] = getChartConfigs(yDomain);
  const interval = amplitude / (numIntervals - 1);

  let ticks = [];
  for (let i = 0; i < numIntervals; i++) {
    const value = min + i * interval;
    ticks.push(value);
  }
  ticks = ticks.map((tick) => Number(tick % 1 === 0 ? tick : tick));
  return ticks;
}

function scalePointPosition(xScale, xPos: number) {
  const domain = xScale.domain();
  const range = xScale.range();
  const rangePoints = d3range(
    range[0] - xScale.step() / 2,
    range[1] + xScale.step() / 2,
    xScale.step(),
  );
  const yPos = domain[bisect(rangePoints, xPos) - 1];
  return yPos;
}

export function getPointValue(mousePos: Position, xScale, xAxisConf) {
  const x = mousePos[0];
  const xValue =
    xAxisConf.type === "point"
      ? scalePointPosition(xScale, x)
      : xAxisConf.type !== "band"
        ? xScale.invert(x)
        : xScale(x);
  return xValue;
}

const prepareXScale = ({ xAxisConf, xField, data, xRange }) => {
  const xDomain: Range =
    xAxisConf.type === "linear" || xAxisConf.type === "time"
      ? [
          min(data, (d: number[]) => d[xField]),
          max(data, (d: number[]) => d[xField]),
        ]
      : ([...new Set(data.map((d: number[]) => d[xField]))] as Range);
  return createScale(xAxisConf.type, {
    domain: xDomain,
    range: xRange,
    nice: false,
    tickCount: xAxisConf.tickCount,
    padding: xAxisConf.padding,
  });
};

const prepareYScale = ({ yAxisConf, yField, data, yRange }) => {
  const yDomain: Range =
    yAxisConf.type === "linear" || yAxisConf.type === "time"
      ? [
          min(data, (d: number[]) => d[yField]),
          max(data, (d: number[]) => d[yField]),
        ]
      : ([...new Set(data.map((d: number[]) => d[yField]))] as Range); // unsafe
  const tickValues =
    yAxisConf.tickValues ||
    (yAxisConf.tickCount && !yAxisConf.useD3Ticks
      ? getTickValues(yDomain, yAxisConf.tickCount)
      : null);

  return {
    yScale: createScale(yAxisConf.type, {
      domain: tickValues
        ? [min([...tickValues, yDomain[0]]), max([...tickValues, yDomain[1]])]
        : yDomain,
      range: yRange,
      nice: yAxisConf.nice,
      tickCount: yAxisConf.tickCount,
      padding: yAxisConf.padding,
    }),
    tickValues,
  };
};

export function prepareScales({
  xAxisConf,
  yAxisConf,
  data,
  xField,
  yField,
  xRange,
  yRange,
}) {
  const xScale = prepareXScale({ xAxisConf, xField, data, xRange });
  const { yScale, tickValues } = prepareYScale({
    yAxisConf,
    yField,
    data,
    yRange,
  });

  return {
    xScale,
    yScale,
    yTicks: tickValues || yScale.ticks(yAxisConf.tickCount),
  };
}
